import React, {useEffect, useMemo, useState} from 'react';
import {
    AlertTriangle,
    CheckCircle,
    Clock,
    ExternalLink,
    Key,
    Link2,
    Package,
    RefreshCw,
    Save,
    Settings,
    Shield,
    ShoppingBag,
    Store,
    Truck,
    X,
    XCircle,
} from 'lucide-react';
import Notification from '../components/Common/Notification';
import secureStorage from '../utils/secureStorage';

const INTEGRATION_PROVIDERS = [
    {
        id: 'wix',
        name: 'Wix',
        description: 'Connectez votre boutique Wix pour synchroniser le catalogue et centraliser les commandes.',
        accent: 'bg-blue-500',
        badge: 'text-blue-600 bg-blue-50 border-blue-200',
        Icon: Store,
        fields: [
            {key: 'siteUrl', label: 'URL de la boutique', placeholder: 'https://votreboutique.wixsite.com/store', required: true},
            {key: 'siteId', label: 'Site ID', placeholder: 'ex: wix-site-01', required: true},
            {key: 'apiKey', label: 'Clé API', placeholder: '••••••••••••••••', required: true, sensitive: true},
            {key: 'webhookSecret', label: 'Webhook secret', placeholder: 'Secret webhook Wix', sensitive: true},
        ],
        syncOptions: [
            {key: 'syncCatalog', label: 'Synchroniser le catalogue'},
            {key: 'syncOrders', label: 'Importer les commandes'},
            {key: 'syncInventory', label: 'Mettre à jour le stock'},
        ],
        highlights: ['Catalogue', 'Commandes', 'Stock'],
    },
    {
        id: 'glovo',
        name: 'Glovo',
        description: 'Gérez les commandes Glovo depuis un seul espace et gardez vos statuts de livraison sous contrôle.',
        accent: 'bg-yellow-400',
        badge: 'text-yellow-700 bg-yellow-50 border-yellow-200',
        Icon: Truck,
        fields: [
            {key: 'storeCode', label: 'Store code', placeholder: 'ex: GLOVO-CASA-01', required: true},
            {key: 'partnerId', label: 'Partner ID', placeholder: 'Identifiant partenaire Glovo', required: true},
            {key: 'apiKey', label: 'Clé API', placeholder: '••••••••••••••••', required: true, sensitive: true},
            {key: 'webhookUrl', label: 'Webhook URL', placeholder: 'https://api.quickcom.ma/glovo/webhook'},
        ],
        syncOptions: [
            {key: 'syncCatalog', label: 'Synchroniser le menu'},
            {key: 'syncOrders', label: 'Importer les commandes'},
            {key: 'syncInventory', label: 'Bloquer les ruptures de stock'},
        ],
        highlights: ['Menu', 'Commandes', 'Disponibilité'],
    },
    {
        id: 'jumiaFood',
        name: 'Jumia Food',
        description: 'Centralisez vos réglages Jumia Food pour piloter les commandes, le menu et la disponibilité.',
        accent: 'bg-orange-500',
        badge: 'text-orange-700 bg-orange-50 border-orange-200',
        Icon: ShoppingBag,
        fields: [
            {key: 'vendorId', label: 'Vendor ID', placeholder: 'Identifiant vendeur Jumia Food', required: true},
            {key: 'branchCode', label: 'Branch code', placeholder: 'ex: JF-RABAT-02', required: true},
            {key: 'apiKey', label: 'Clé API', placeholder: '••••••••••••••••', required: true, sensitive: true},
            {key: 'callbackUrl', label: 'Callback URL', placeholder: 'https://api.quickcom.ma/jumia-food/callback'},
        ],
        syncOptions: [
            {key: 'syncCatalog', label: 'Synchroniser le menu'},
            {key: 'syncOrders', label: 'Récupérer les commandes'},
            {key: 'syncInventory', label: 'Synchroniser la disponibilité'},
        ],
        highlights: ['Menu', 'Commandes', 'Disponibilité'],
    },
];

const buildStorageKey = (accountId) => `integrations_${accountId}`;

const buildEmptyValues = (provider) =>
    provider.fields.reduce((acc, field) => ({
        ...acc,
        [field.key]: '',
    }), {});

const buildDefaultConfigurations = () =>
    INTEGRATION_PROVIDERS.reduce((acc, provider) => ({
        ...acc,
        [provider.id]: {
            values: buildEmptyValues(provider),
            isEnabled: false,
            syncCatalog: true,
            syncOrders: true,
            syncInventory: provider.id !== 'wix',
            lastUpdatedAt: null,
            lastSyncAt: null,
        },
    }), {});

const mergeConfigurations = (storedConfigurations) => {
    const defaults = buildDefaultConfigurations();

    return INTEGRATION_PROVIDERS.reduce((acc, provider) => ({
        ...acc,
        [provider.id]: {
            ...defaults[provider.id],
            ...(storedConfigurations?.[provider.id] || {}),
            values: {
                ...defaults[provider.id].values,
                ...(storedConfigurations?.[provider.id]?.values || {}),
            },
        },
    }), {});
};

const formatDate = (value) => {
    if (!value) return 'Jamais';

    try {
        return new Date(value).toLocaleString('fr-FR', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return 'Jamais';
    }
};

const maskValue = (field, value) => {
    if (!value) return 'Non renseigné';
    if (!field.sensitive) return value;
    if (value.length <= 4) return '••••';
    return `${'•'.repeat(Math.min(10, value.length - 4))}${value.slice(-4)}`;
};

const isProviderConfigured = (provider, configuration) =>
    provider.fields
        .filter((field) => field.required)
        .every((field) => (configuration?.values?.[field.key] || '').trim());

const IntegrationConfigModal = ({provider, configuration, isSaving, onClose, onChangeField, onToggleOption, onSave}) => {
    const configured = isProviderConfigured(provider, configuration);
    const ProviderIcon = provider.Icon;

    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-gray-800">
                <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
                    <div className="flex items-center gap-3">
                        <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${provider.accent} text-white`}>
                            <ProviderIcon size={20}/>
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Configurer {provider.name}</h2>
                            <p className="text-sm text-gray-500 dark:text-gray-400">{provider.description}</p>
                        </div>
                    </div>

                    <button
                        onClick={onClose}
                        className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                        aria-label="Fermer"
                    >
                        <X size={18}/>
                    </button>
                </div>

                <div className="max-h-[75vh] overflow-y-auto px-5 py-5">
                    <div className="mb-5 flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4 dark:border-blue-900 dark:bg-blue-900/20">
                        <Shield size={18} className="mt-0.5 shrink-0 text-blue-600 dark:text-blue-400"/>
                        <div>
                            <p className="text-sm font-medium text-blue-800 dark:text-blue-300">Configuration sécurisée</p>
                            <p className="mt-1 text-sm text-blue-700 dark:text-blue-400">
                                Les identifiants sont enregistrés par compte pour préparer l’activation de l’intégration.
                            </p>
                        </div>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2">
                        {provider.fields.map((field) => (
                            <label key={field.key} className="block">
                                <span className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">
                                    {field.sensitive ? <Key size={14} className="text-gray-400"/> : <Settings size={14} className="text-gray-400"/>}
                                    {field.label}
                                    {field.required && <span className="text-red-500">*</span>}
                                </span>
                                <input
                                    type="text"
                                    value={configuration.values[field.key] || ''}
                                    onChange={(event) => onChangeField(field.key, event.target.value)}
                                    placeholder={field.placeholder}
                                    className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm text-gray-800 outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                                />
                            </label>
                        ))}
                    </div>

                    <div className="mt-6 rounded-2xl border border-gray-200 p-4 dark:border-gray-700">
                        <div className="mb-3 flex items-center gap-2">
                            <RefreshCw size={16} className="text-primary"/>
                            <h3 className="text-sm font-semibold text-gray-800 dark:text-white">Options de synchronisation</h3>
                        </div>

                        <div className="space-y-3">
                            {provider.syncOptions.map((option) => (
                                <label key={option.key} className="flex items-center justify-between gap-3 rounded-xl bg-gray-50 px-3 py-3 dark:bg-gray-700/50">
                                    <div>
                                        <p className="text-sm font-medium text-gray-800 dark:text-white">{option.label}</p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">Activez cette synchronisation pour {provider.name}.</p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => onToggleOption(option.key)}
                                        className={`relative h-6 w-11 rounded-full transition-colors ${configuration[option.key] ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'}`}
                                        aria-label={option.label}
                                    >
                                        <span
                                            className={`absolute top-1 h-4 w-4 rounded-full bg-white shadow transition-transform ${configuration[option.key] ? 'translate-x-6' : 'translate-x-1'}`}
                                        />
                                    </button>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div className={`mt-5 rounded-xl border px-4 py-3 ${configured ? 'border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-900/20' : 'border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-900/20'}`}>
                        <div className="flex items-start gap-3">
                            {configured ? (
                                <CheckCircle size={18} className="mt-0.5 shrink-0 text-green-600 dark:text-green-400"/>
                            ) : (
                                <AlertTriangle size={18} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400"/>
                            )}
                            <div>
                                <p className={`text-sm font-medium ${configured ? 'text-green-800 dark:text-green-300' : 'text-amber-800 dark:text-amber-300'}`}>
                                    {configured ? 'Prêt à être connecté' : 'Configuration incomplète'}
                                </p>
                                <p className={`mt-1 text-sm ${configured ? 'text-green-700 dark:text-green-400' : 'text-amber-700 dark:text-amber-400'}`}>
                                    {configured
                                        ? `Vous pouvez maintenant activer ${provider.name} depuis la carte d’intégration.`
                                        : 'Complétez tous les champs obligatoires pour activer cette intégration.'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-5 py-4 dark:border-gray-700">
                    <button
                        onClick={onClose}
                        className="rounded-xl px-4 py-2 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                    >
                        Annuler
                    </button>
                    <button
                        onClick={onSave}
                        disabled={isSaving}
                        className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {isSaving ? <RefreshCw size={16} className="animate-spin"/> : <Save size={16}/>}
                        {isSaving ? 'Enregistrement...' : 'Enregistrer'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const IntegrationsPage = () => {
    const [account, setAccount] = useState(null);
    const [configurations, setConfigurations] = useState(() => buildDefaultConfigurations());
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [activeProviderId, setActiveProviderId] = useState(null);
    const [draftConfiguration, setDraftConfiguration] = useState(null);
    const [notification, setNotification] = useState({show: false, type: 'success', message: ''});

    const activeProvider = INTEGRATION_PROVIDERS.find((provider) => provider.id === activeProviderId) || null;

    useEffect(() => {
        document.title = 'Intégrations - Dashboard';
    }, []);

    useEffect(() => {
        let isMounted = true;

        const loadIntegrations = async () => {
            try {
                const selectedAccount = await secureStorage.getItem('selectedAccount');

                if (!selectedAccount?.id) {
                    if (isMounted) {
                        setAccount(null);
                        setConfigurations(buildDefaultConfigurations());
                    }
                    return;
                }

                const storedConfigurations = await secureStorage.getItem(buildStorageKey(selectedAccount.id));

                if (isMounted) {
                    setAccount(selectedAccount);
                    setConfigurations(mergeConfigurations(storedConfigurations));
                }
            } catch (error) {
                console.error('Erreur lors du chargement des intégrations:', error);
                if (isMounted) {
                    setNotification({
                        show: true,
                        type: 'error',
                        message: 'Impossible de charger les intégrations du compte.',
                    });
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        loadIntegrations();

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        if (!notification.show || notification.type === 'loading') {
            return undefined;
        }

        const timer = window.setTimeout(() => {
            setNotification((current) => ({...current, show: false}));
        }, 3000);

        return () => window.clearTimeout(timer);
    }, [notification]);

    const persistConfigurations = async (nextConfigurations, successMessage) => {
        if (!account?.id) {
            setNotification({show: true, type: 'error', message: 'Aucun compte sélectionné.'});
            return false;
        }

        setIsSaving(true);

        try {
            const saved = await secureStorage.setItem(buildStorageKey(account.id), nextConfigurations);

            if (!saved) {
                throw new Error('Enregistrement impossible');
            }

            setConfigurations(nextConfigurations);
            setNotification({show: true, type: 'success', message: successMessage});
            return true;
        } catch (error) {
            console.error('Erreur lors de la sauvegarde des intégrations:', error);
            setNotification({show: true, type: 'error', message: 'La sauvegarde a échoué. Veuillez réessayer.'});
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const openConfiguration = (providerId) => {
        setActiveProviderId(providerId);
        setDraftConfiguration({...configurations[providerId]});
    };

    const closeConfiguration = () => {
        if (isSaving) return;
        setActiveProviderId(null);
        setDraftConfiguration(null);
    };

    const handleDraftFieldChange = (fieldKey, value) => {
        setDraftConfiguration((current) => ({
            ...current,
            values: {
                ...current.values,
                [fieldKey]: value,
            },
        }));
    };

    const handleDraftToggle = (optionKey) => {
        setDraftConfiguration((current) => ({
            ...current,
            [optionKey]: !current[optionKey],
        }));
    };

    const handleSaveConfiguration = async () => {
        if (!activeProvider || !draftConfiguration) return;

        const configured = isProviderConfigured(activeProvider, draftConfiguration);
        const currentConfiguration = configurations[activeProvider.id];
        const nextConfiguration = {
            ...draftConfiguration,
            isEnabled: configured ? currentConfiguration.isEnabled : false,
            lastUpdatedAt: new Date().toISOString(),
        };
        const nextConfigurations = {
            ...configurations,
            [activeProvider.id]: nextConfiguration,
        };

        const successMessage = configured
            ? `${activeProvider.name} a été configuré avec succès.`
            : `${activeProvider.name} a été enregistré comme brouillon.`;

        const saved = await persistConfigurations(nextConfigurations, successMessage);
        if (saved) {
            closeConfiguration();
        }
    };

    const handleConnectionToggle = async (provider) => {
        const currentConfiguration = configurations[provider.id];
        const configured = isProviderConfigured(provider, currentConfiguration);

        if (!currentConfiguration.isEnabled && !configured) {
            setNotification({
                show: true,
                type: 'error',
                message: `Complétez d’abord la configuration ${provider.name} avant de l’activer.`,
            });
            openConfiguration(provider.id);
            return;
        }

        const nextConfigurations = {
            ...configurations,
            [provider.id]: {
                ...currentConfiguration,
                isEnabled: !currentConfiguration.isEnabled,
                lastUpdatedAt: new Date().toISOString(),
                lastSyncAt: !currentConfiguration.isEnabled ? new Date().toISOString() : currentConfiguration.lastSyncAt,
            },
        };

        await persistConfigurations(
            nextConfigurations,
            currentConfiguration.isEnabled
                ? `${provider.name} a été désactivé.`
                : `${provider.name} est maintenant connecté.`
        );
    };

    const handleSyncNow = async (provider) => {
        const currentConfiguration = configurations[provider.id];

        if (!currentConfiguration.isEnabled) {
            setNotification({
                show: true,
                type: 'error',
                message: `Activez ${provider.name} avant de lancer une synchronisation.`,
            });
            return;
        }

        const nextConfigurations = {
            ...configurations,
            [provider.id]: {
                ...currentConfiguration,
                lastUpdatedAt: new Date().toISOString(),
                lastSyncAt: new Date().toISOString(),
            },
        };

        await persistConfigurations(nextConfigurations, `Synchronisation ${provider.name} enregistrée.`);
    };

    const stats = useMemo(() => {
        const total = INTEGRATION_PROVIDERS.length;
        const connected = INTEGRATION_PROVIDERS.filter((provider) => configurations[provider.id]?.isEnabled).length;
        const configured = INTEGRATION_PROVIDERS.filter((provider) => isProviderConfigured(provider, configurations[provider.id])).length;

        return {
            total,
            connected,
            configured,
            pending: total - configured,
        };
    }, [configurations]);

    return (
        <div className="min-h-screen bg-gray-50 px-4 py-6 dark:bg-gray-900 sm:px-6">
            <Notification
                show={notification.show}
                type={notification.type}
                message={notification.message}
                onClose={() => setNotification((current) => ({...current, show: false}))}
            />

            <div className="mx-auto max-w-7xl space-y-6">
                <div className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-gray-200 dark:bg-gray-800 dark:ring-gray-700">
                    <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
                        <div className="max-w-3xl">
                            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
                                <Link2 size={16}/>
                                Centre d’intégrations
                            </div>
                            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white sm:text-3xl">Intégrations</h1>
                            <p className="mt-3 text-sm leading-6 text-gray-600 dark:text-gray-300 sm:text-base">
                                Gérez vos connexions avec <strong>Wix</strong>, <strong>Glovo</strong> et <strong>Jumia Food</strong>
                                pour centraliser vos catalogues, vos commandes et vos synchronisations métier.
                            </p>
                            <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                                <span className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 dark:bg-gray-700">
                                    <Shield size={14}/>
                                    Compte : {account?.name || 'Non défini'}
                                </span>
                                <span className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 dark:bg-gray-700">
                                    <Package size={14}/>
                                    {stats.connected} / {stats.total} connectée(s)
                                </span>
                            </div>
                        </div>

                        <div className="grid min-w-full gap-3 sm:grid-cols-2 lg:min-w-[320px]">
                            {[
                                {
                                    label: 'Intégrations actives',
                                    value: stats.connected,
                                    helper: 'Connexions prêtes à l’emploi',
                                    icon: <CheckCircle size={18} className="text-green-600 dark:text-green-400"/>,
                                    tone: 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300',
                                },
                                {
                                    label: 'Configurations prêtes',
                                    value: stats.configured,
                                    helper: 'Fiches complètes',
                                    icon: <Settings size={18} className="text-blue-600 dark:text-blue-400"/>,
                                    tone: 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300',
                                },
                                {
                                    label: 'À compléter',
                                    value: stats.pending,
                                    helper: 'Clés ou IDs manquants',
                                    icon: <AlertTriangle size={18} className="text-amber-600 dark:text-amber-400"/>,
                                    tone: 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300',
                                },
                                {
                                    label: 'Dernière activité',
                                    value: INTEGRATION_PROVIDERS.some((provider) => configurations[provider.id]?.lastSyncAt) ? 'OK' : '—',
                                    helper: 'Synchronisation suivie',
                                    icon: <Clock size={18} className="text-primary"/>,
                                    tone: 'bg-primary/10 text-primary dark:bg-primary/10 dark:text-primary',
                                },
                            ].map((card) => (
                                <div key={card.label} className={`rounded-2xl p-4 ${card.tone}`}>
                                    <div className="flex items-center justify-between gap-3">
                                        <div>
                                            <p className="text-xs font-medium uppercase tracking-wide opacity-80">{card.label}</p>
                                            <p className="mt-2 text-2xl font-semibold">{card.value}</p>
                                        </div>
                                        <div className="rounded-xl bg-white/70 p-2 dark:bg-gray-800/60">{card.icon}</div>
                                    </div>
                                    <p className="mt-2 text-xs opacity-80">{card.helper}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="grid gap-5 xl:grid-cols-3">
                    {INTEGRATION_PROVIDERS.map((provider) => {
                        const ProviderIcon = provider.Icon;
                        const configuration = configurations[provider.id];
                        const configured = isProviderConfigured(provider, configuration);
                        const missingFields = provider.fields.filter((field) => field.required && !(configuration?.values?.[field.key] || '').trim()).length;
                        const statusClass = configuration.isEnabled
                            ? 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-900'
                            : configured
                                ? 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-900'
                                : 'bg-gray-50 text-gray-600 border-gray-200 dark:bg-gray-700/50 dark:text-gray-300 dark:border-gray-600';
                        const statusLabel = configuration.isEnabled
                            ? 'Connecté'
                            : configured
                                ? 'Prêt à connecter'
                                : 'Brouillon';

                        return (
                            <div
                                key={provider.id}
                                className="overflow-hidden rounded-3xl bg-white shadow-sm ring-1 ring-gray-200 transition-transform hover:-translate-y-0.5 dark:bg-gray-800 dark:ring-gray-700"
                            >
                                <div className="border-b border-gray-100 p-5 dark:border-gray-700">
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex items-start gap-4">
                                            <div className={`flex h-14 w-14 items-center justify-center rounded-2xl ${provider.accent} text-white shadow-sm`}>
                                                <ProviderIcon size={24}/>
                                            </div>
                                            <div>
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{provider.name}</h2>
                                                    <span className={`rounded-full border px-2.5 py-1 text-xs font-medium ${statusClass}`}>
                                                        {statusLabel}
                                                    </span>
                                                </div>
                                                <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">{provider.description}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-4 flex flex-wrap gap-2">
                                        {provider.highlights.map((highlight) => (
                                            <span
                                                key={highlight}
                                                className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium ${provider.badge}`}
                                            >
                                                {highlight}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                <div className="space-y-4 p-5">
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="rounded-2xl bg-gray-50 p-4 dark:bg-gray-700/40">
                                            <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Dernière mise à jour</p>
                                            <p className="mt-2 text-sm font-medium text-gray-900 dark:text-white">{formatDate(configuration.lastUpdatedAt)}</p>
                                        </div>
                                        <div className="rounded-2xl bg-gray-50 p-4 dark:bg-gray-700/40">
                                            <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Dernière synchro</p>
                                            <p className="mt-2 text-sm font-medium text-gray-900 dark:text-white">{formatDate(configuration.lastSyncAt)}</p>
                                        </div>
                                    </div>

                                    <div className="rounded-2xl border border-gray-200 p-4 dark:border-gray-700">
                                        <div className="mb-3 flex items-center justify-between gap-3">
                                            <h3 className="text-sm font-semibold text-gray-800 dark:text-white">Configuration</h3>
                                            {configured ? (
                                                <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600 dark:text-green-400">
                                                    <CheckCircle size={14}/>
                                                    Complète
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600 dark:text-amber-400">
                                                    <AlertTriangle size={14}/>
                                                    {missingFields} champ(s) requis
                                                </span>
                                            )}
                                        </div>

                                        <div className="space-y-2.5">
                                            {provider.fields.slice(0, 3).map((field) => (
                                                <div key={field.key} className="flex items-center justify-between gap-3 text-sm">
                                                    <span className="text-gray-500 dark:text-gray-400">{field.label}</span>
                                                    <span className="max-w-[55%] truncate text-right font-medium text-gray-800 dark:text-gray-200">
                                                        {maskValue(field, configuration.values[field.key])}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="rounded-2xl border border-gray-200 p-4 dark:border-gray-700">
                                        <div className="mb-3 flex items-center gap-2">
                                            <RefreshCw size={16} className="text-primary"/>
                                            <h3 className="text-sm font-semibold text-gray-800 dark:text-white">Synchronisation active</h3>
                                        </div>
                                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                                            {provider.syncOptions.map((option) => (
                                                <div key={option.key} className="rounded-xl bg-gray-50 px-3 py-2 dark:bg-gray-700/40">
                                                    <p className="text-xs text-gray-500 dark:text-gray-400">{option.label}</p>
                                                    <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                                                        {configuration[option.key] ? 'Activée' : 'Inactive'}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="flex flex-col gap-3 sm:flex-row">
                                        <button
                                            onClick={() => openConfiguration(provider.id)}
                                            className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                                        >
                                            <Settings size={16}/>
                                            Configurer
                                        </button>
                                        <button
                                            onClick={() => handleConnectionToggle(provider)}
                                            disabled={isSaving}
                                            className={`inline-flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium text-white transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${configuration.isEnabled ? 'bg-red-500 hover:bg-red-600' : 'bg-primary hover:bg-primary/90'}`}
                                        >
                                            {configuration.isEnabled ? <XCircle size={16}/> : <CheckCircle size={16}/>}
                                            {configuration.isEnabled ? 'Déconnecter' : 'Connecter'}
                                        </button>
                                    </div>

                                    <button
                                        onClick={() => handleSyncNow(provider)}
                                        disabled={isSaving || !configuration.isEnabled}
                                        className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-black disabled:cursor-not-allowed disabled:opacity-40 dark:bg-gray-700 dark:hover:bg-gray-600"
                                    >
                                        <RefreshCw size={16}/>
                                        Synchroniser maintenant
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="rounded-3xl border border-dashed border-gray-300 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Pilotage centralisé</h2>
                            <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">
                                Utilisez cette page pour préparer vos identifiants, activer vos connecteurs et suivre la dernière
                                synchronisation pour chaque plateforme partenaire.
                            </p>
                        </div>

                        <div className="flex flex-wrap gap-3 text-sm">
                            <div className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-2 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                                <ExternalLink size={14}/>
                                Wix, Glovo, Jumia Food
                            </div>
                            <div className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-2 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                                <Shield size={14}/>
                                Configuration par compte
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {activeProvider && draftConfiguration && (
                <IntegrationConfigModal
                    provider={activeProvider}
                    configuration={draftConfiguration}
                    isSaving={isSaving}
                    onClose={closeConfiguration}
                    onChangeField={handleDraftFieldChange}
                    onToggleOption={handleDraftToggle}
                    onSave={handleSaveConfiguration}
                />
            )}

            {isLoading && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/20 backdrop-blur-sm">
                    <div className="inline-flex items-center gap-3 rounded-2xl bg-white px-5 py-4 text-sm font-medium text-gray-700 shadow-lg dark:bg-gray-800 dark:text-gray-200">
                        <RefreshCw size={18} className="animate-spin text-primary"/>
                        Chargement des intégrations...
                    </div>
                </div>
            )}
        </div>
    );
};

export default IntegrationsPage;

